import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import { Box, Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CommonDataGrid from '../../../shared/components/data-grid';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import {
  RMSTextField,
  RMSSelect,
  RMSCheckbox,
  RMSDatePicker,
} from '../../../shared/components/form-controls';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import SectionLabel from './SectionLabel';
import AddressPrePlanInspectionDialog from './AddressPrePlanInspectionDialog';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import {
  getAddressPrePlan,
  saveAddressPrePlan,
  getPrePlanInspections,
  deletePrePlanInspection,
} from '../../services/userService';
import {
  DICT_SELECT_PROPS,
  DEFAULT_VALUES,
  INSPECTION_GRID_COLUMNS,
} from '../../constants/addressPrePlanFormConstants';

const AddressPrePlanTab = forwardRef(({ ptsAddressID, onStateChange }, ref) => {
  const dispatch = useDispatch();
  const canEditAddress = getFullPermissions('globals', 'Addresses', 'any').Edit;
  const isViewOnly = !canEditAddress;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [prePlan, setPrePlan] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [inspectionFormOpen, setInspectionFormOpen] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const {
    prePlanOccupancyTypeOptions,
    prePlanConstructionTypeOptions,
    prePlanRoofTypeOptions,
  } = useUserDictionaryOptions();

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: DEFAULT_VALUES,
    disabled: isViewOnly,
  });

  const { isDirty } = useFormState({ control: methods.control });
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadPrePlan = useCallback(async () => {
    if (!ptsAddressID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getAddressPrePlan(ptsAddressID),
      onSuccess: (data) => {
        setPrePlan(data || null);
        methods.reset({
          ...DEFAULT_VALUES,
          ...(data || {}),
        });
      },
    });
  }, [dispatch, ptsAddressID, methods]);

  const loadInspections = useCallback(async () => {
    if (!ptsAddressID) return;

    await executeApiAction({
      dispatch,
      action: () => getPrePlanInspections(ptsAddressID),
      onSuccess: (data) => setInspections(data || []),
    });
  }, [dispatch, ptsAddressID]);

  useEffect(() => {
    loadPrePlan();
    loadInspections();
  }, [loadPrePlan, loadInspections]);

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsAddressID,
        OrigPrePlanDate: data.OrigPrePlanDate || null,
        OccupancyType: data.OccupancyType || null,
        ConstructionType: data.ConstructionType || null,
        RoofType: data.RoofType || null,
        BuildingCapacity: data.BuildingCapacity || null,
        NumberFloors: data.NumberFloors || null,
        NumberOfExits: data.NumberOfExits || null,
        RequiredFlow: data.RequiredFlow || null,
        AvailableFlow: data.AvailableFlow || null,
        Notes: data.Notes || null,
        Firewalls: Boolean(data.Firewalls),
        GasService: Boolean(data.GasService),
        GasCutoffLocation: data.GasCutoffLocation || null,
        ElectricalService: Boolean(data.ElectricalService),
        ElectricalCutoffLocation: data.ElectricalCutoffLocation || null,
        Sprinkler: Boolean(data.Sprinkler),
        SprinklerShutoffLocation: data.SprinklerShutoffLocation || null,
        SprinklerFDConnectionLocation: data.SprinklerFDConnectionLocation || null,
        Standpipe: Boolean(data.Standpipe),
        StandpipeShutoffLocation: data.StandpipeShutoffLocation || null,
        StandpipeFDConnectionLocation: data.StandpipeFDConnectionLocation || null,
      };

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => saveAddressPrePlan(payload),
        onSuccess: () => {
          dispatch(notify('Pre-Plan saved successfully', 'success'));
          loadPrePlan();
        },
      });
    },
    [ptsAddressID, dispatch, loadPrePlan]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  useImperativeHandle(
    ref,
    () => ({
      isDirty: () => isDirtyRef.current,
      save: () => handleSave(),
    }),
    [handleSave]
  );

  useEffect(() => {
    onStateChange?.({ prePlan, submitting: submitting || loading });
  }, [prePlan, submitting, loading, onStateChange]);

  const handleAddInspection = useCallback(() => {
    setEditingInspection(null);
    setInspectionFormOpen(true);
  }, []);

  const handleEditInspection = useCallback((row) => {
    setEditingInspection(row);
    setInspectionFormOpen(true);
  }, []);

  const handleInspectionFormClose = useCallback(() => {
    setInspectionFormOpen(false);
    setEditingInspection(null);
  }, []);

  const handleInspectionSaved = useCallback(() => {
    handleInspectionFormClose();
    loadInspections();
  }, [handleInspectionFormClose, loadInspections]);

  const handleDeleteInspection = useCallback(
    (row) => {
      requestConfirm(
        'Are you sure you want to delete this inspection?',
        async () => {
          await executeApiAction({
            setLoading: setDeleting,
            dispatch,
            action: () => deletePrePlanInspection(row.id),
            onSuccess: () => {
              dispatch(notify('Inspection deleted successfully', 'success'));
              loadInspections();
            },
          });
        },
        'Delete Inspection'
      );
    },
    [dispatch, requestConfirm, loadInspections]
  );

  return (
    <Box>
      <Box className="rms-dialog-section">
        <SectionLabel>Pre-Plan</SectionLabel>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <RMSDatePicker name="OrigPrePlanDate" label="Original Pre-Plan Date" />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="OccupancyType"
                label="Occupancy Type"
                options={prePlanOccupancyTypeOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="ConstructionType"
                label="Construction Type"
                options={prePlanConstructionTypeOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSSelect
                name="RoofType"
                label="Roof Type"
                options={prePlanRoofTypeOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="BuildingCapacity"
                label="Building Capacity"
                type="number"
                inputProps={{ min: 0 }}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="NumberFloors"
                label="Number Of Floors"
                type="number"
                inputProps={{ min: 0 }}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="NumberOfExits"
                label="Number Of Exits"
                type="number"
                inputProps={{ min: 0 }}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={3} />
            <Grid item xs={3}>
              <RMSTextField
                name="RequiredFlow"
                label="Required Flow"
                type="number"
                inputProps={{ min: 0 }}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={3}>
              <RMSTextField
                name="AvailableFlow"
                label="Available Flow"
                type="number"
                inputProps={{ min: 0 }}
                shrinkLabel
              />
            </Grid>
            <Grid item xs={12}>
              <RMSTextField name="Notes" label="Notes" multiline rows={2} />
            </Grid>

            <Grid item xs={3}>
              <RMSCheckbox name="Firewalls" label="Firewalls" />
            </Grid>
            <Grid item xs={3}>
              <RMSCheckbox name="GasService" label="Gas Service" />
            </Grid>
            <Grid item xs={6}>
              <RMSTextField
                name="GasCutoffLocation"
                label="Gas Cutoff Location"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSCheckbox name="ElectricalService" label="Electrical Service" />
            </Grid>
            <Grid item xs={9}>
              <RMSTextField
                name="ElectricalCutoffLocation"
                label="Electrical Cutoff Location"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSCheckbox name="Sprinkler" label="Sprinkler" />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField
                name="SprinklerShutoffLocation"
                label="Sprinkler Shutoff Location"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={5}>
              <RMSTextField
                name="SprinklerFDConnectionLocation"
                label="Sprinkler FD Connection Location"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={3}>
              <RMSCheckbox name="Standpipe" label="Standpipe" />
            </Grid>
            <Grid item xs={4}>
              <RMSTextField name="StandpipeShutoffLocation" label="Standpipe Shutoff Location" />
            </Grid>
            <Grid item xs={5}>
              <RMSTextField
                name="StandpipeFDConnectionLocation"
                label="Standpipe FD Connection Location"
              />
            </Grid>
          </Grid>
        </FormProvider>
      </Box>

      <Box className="person-names-tab__grid-wrap">
        <Box className="person-addresses-tab__toolbar">
          <RMSButton
            label="Add Inspection"
            prefixIcon={<AddIcon />}
            disabled={!prePlan?.ptsAddressPrePlanID || !canEditAddress}
            onClick={handleAddInspection}
          />
        </Box>

        <CommonDataGrid
          rows={inspections}
          columns={INSPECTION_GRID_COLUMNS}
          getRowId={(row) => row.ptsAddressPrePlanInspectionID || row.id}
          autoHeight
          toolbar={{
            showSearch: true,
            showColumnSelector: true,
            showFilter: true,
            showDensity: true,
            showExport: true,
            printTitle: 'Pre-Plan Inspections',
          }}
          actions={{
            edit: { visible: true, onClick: handleEditInspection, disabled: !canEditAddress },
            delete: { visible: true, onClick: handleDeleteInspection, disabled: !canEditAddress },
          }}
        />
      </Box>

      {inspectionFormOpen && (
        <AddressPrePlanInspectionDialog
          open={inspectionFormOpen}
          ptsAddressPrePlanID={prePlan?.ptsAddressPrePlanID}
          inspection={editingInspection}
          onClose={handleInspectionFormClose}
          onSaved={handleInspectionSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
});

export default AddressPrePlanTab;
